<script setup lang="ts">
import { computed, toRef } from 'vue';
import type { Author } from '@models/author';
import type { AccountManager } from '@models/accountManager';
import MediaImage from '@components/MediaImage.vue';
import { useAuthor } from '@composables/useAuthor';
import { type Color, getColorValue } from '@global-types/colors';
import type { BasicUser } from '@models/basicUser';
import type { TypedUser } from '@models/typedUser';

const props = withDefaults(defineProps<{
  user: Author | TypedUser | AccountManager | BasicUser,
  size?: number,
}>(), {
  size: 24,
});

const initials = computed<string>(() => {
  const { firstname } = props.user;
  return firstname[0].toUpperCase();
  // @todo Standby for now, specs to come
  // const lastname = props.user.lastname;
  // return firstname[0].toUpperCase() + lastname[0].toUpperCase();
});

const user = toRef(() => props.user);

const { pictureUrl, mediaUuid } = useAuthor(user);

const colors: Color[] = [ 'green', 'blue', 'red', 'yellow', 'orange', 'black', 'purple' ];

const backgroundColor = computed<string>(() => {
  const letter = initials.value[0];
  if (!letter) {
    return 'black';
  }
  const normalized = letter.toUpperCase().charCodeAt(0);
  const index = normalized % colors.length;
  return getColorValue(colors[index]);
});
</script>

<template>
  <div class="user-picture">
    <div
      v-if="pictureUrl"
      class="user-picture__image picture-image"
    >
      <img
        class="picture-image__media"
        :style="{ width: `${size}px`, height: `${size}px` }"
        :src="pictureUrl"
        :alt="user.full_name"
      >
    </div>
    <div
      v-else-if="mediaUuid"
      class="user-picture__image picture-image"
      :style="{ width: `${size}px`, height: `${size}px` }"
    >
      <MediaImage
        class="picture-image__media"
        :uuid="mediaUuid"
        :alt="user.full_name"
        :ratio="[ 1, 1 ]"
        :width="size"
        :height="size"
        :crop="[ size, size ]"
        :draggable="false"
      />
    </div>
    <div
      v-else
      class="user-picture__initials"
      :style="{ width: `${size}px`, height: `${size}px`, backgroundColor }"
    >
      {{ initials }}
    </div>
  </div>
</template>
